/**
 * Branding Cache Integration Service
 * 
 * Integrates the existing branding cache system (useBranding.ts) with our 
 * atomic reset infrastructure. Provides enhanced cache invalidation, 
 * community-aware clearing, and TTL management for branding data.
 * 
 * Phase 3 implementation - enhances existing branding with cache invalidation
 */

import { QueryClient } from '@tanstack/react-query';
import type { ClientRpcBrandingPayload } from '@/lib/types/branding';

// Branding storage keys (from useBranding.ts)
const BRANDING_STORAGE_KEY = 'terrarium_community_branding_v4';
const CRITICAL_BRANDING_STORAGE_KEY = 'terrarium_critical_branding_v4';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// TTL for branding cache (1 hour in milliseconds)
const BRANDING_CACHE_TTL = 60 * 60 * 1000;

/**
 * Interface for branding cache data with TTL support
 */
interface BrandingCacheEntry {
  hostname: string;
  rpcPayload: ClientRpcBrandingPayload;
  timestamp: number;
  metaVersion?: string;
}

/**
 * Interface for branding invalidation result
 */
export interface BrandingInvalidationResult {
  success: boolean;
  keysCleared: string[];
  queryKeysCleared: string[];
  errors: string[];
}

/**
 * Get the React Query key for branding data
 */
function getBrandingQueryKey(hostname: string): string[] {
  return ['branding', hostname];
}

/**
 * Check if a branding cache entry has expired based on TTL
 */
function isBrandingCacheExpired(entry: BrandingCacheEntry): boolean {
  const now = Date.now();
  const age = now - entry.timestamp;
  return age > BRANDING_CACHE_TTL;
}

/**
 * Enhanced branding cache getter with TTL support
 */
export function getBrandingFromCache(hostname: string): ClientRpcBrandingPayload | null {
  try {
    const cached = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (!cached) {
      return null;
    }

    const parsed: BrandingCacheEntry = JSON.parse(cached);
    
    // Check hostname match
    if (parsed.hostname !== hostname) {
      return null;
    }

    // Check TTL expiration
    if (isBrandingCacheExpired(parsed)) {
      if (isDevelopment) {
        console.log('BrandingCache: Entry expired for hostname', hostname);
      }
      localStorage.removeItem(BRANDING_STORAGE_KEY);
      return null;
    }

    if (isDevelopment) {
      const age = Date.now() - parsed.timestamp;
      console.log(`BrandingCache: Retrieved cached branding for ${hostname} (age: ${age}ms)`);
    }

    return parsed.rpcPayload;
  } catch (error) {
    console.error('BrandingCache: Failed to get cached branding:', error);
    localStorage.removeItem(BRANDING_STORAGE_KEY);
    return null;
  }
}

/**
 * Enhanced branding cache setter with TTL and meta-version support
 */
export function setBrandingInCache(
  hostname: string, 
  payload: ClientRpcBrandingPayload, 
  metaVersion?: string
): void {
  try {
    const entry: BrandingCacheEntry = {
      hostname,
      rpcPayload: payload,
      timestamp: Date.now(),
      metaVersion
    };

    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(entry));

    if (isDevelopment) {
      console.log(`BrandingCache: Saved branding for ${hostname} with meta-version ${metaVersion}`);
    }
  } catch (error) {
    console.error('BrandingCache: Failed to save branding to cache:', error);
  }
}

/**
 * Clear all branding cache entries
 */
export function clearBrandingCache(): string[] {
  const clearedKeys: string[] = [];

  try {
    // Clear localStorage branding keys
    const brandingKeys = [BRANDING_STORAGE_KEY, CRITICAL_BRANDING_STORAGE_KEY];
    
    brandingKeys.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedKeys.push(key);
      }
    });

    if (isDevelopment && clearedKeys.length > 0) {
      console.log('BrandingCache: Cleared localStorage keys:', clearedKeys);
    }
  } catch (error) {
    console.error('BrandingCache: Failed to clear localStorage:', error);
  }

  return clearedKeys;
}

/**
 * Clear branding cache for a specific community/hostname
 */
export function clearBrandingCacheForHostname(hostname: string): string[] {
  const clearedKeys: string[] = [];

  try {
    const cached = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (cached) {
      const parsed: BrandingCacheEntry = JSON.parse(cached);
      if (parsed.hostname === hostname) {
        localStorage.removeItem(BRANDING_STORAGE_KEY);
        clearedKeys.push(BRANDING_STORAGE_KEY);
      }
    }

    // Critical branding is hostname-independent, but clear it too for safety
    if (localStorage.getItem(CRITICAL_BRANDING_STORAGE_KEY) !== null) {
      localStorage.removeItem(CRITICAL_BRANDING_STORAGE_KEY);
      clearedKeys.push(CRITICAL_BRANDING_STORAGE_KEY);
    }

    if (isDevelopment && clearedKeys.length > 0) {
      console.log(`BrandingCache: Cleared branding for hostname ${hostname}:`, clearedKeys);
    }
  } catch (error) {
    console.error('BrandingCache: Failed to clear hostname-specific cache:', error);
  }

  return clearedKeys;
}

/**
 * Clear React Query branding queries
 */
export function clearBrandingQueries(
  queryClient: QueryClient, 
  hostname?: string
): string[] {
  const clearedQueryKeys: string[] = [];

  try {
    if (hostname) {
      // Clear specific hostname branding query
      const queryKey = getBrandingQueryKey(hostname);
      queryClient.removeQueries({ queryKey });
      clearedQueryKeys.push(queryKey.join(':'));
    } else {
      // Clear all branding queries
      queryClient.removeQueries({ 
        predicate: (query) => {
          return Array.isArray(query.queryKey) && 
                 query.queryKey.length > 0 && 
                 query.queryKey[0] === 'branding';
        }
      });
      clearedQueryKeys.push('branding:*');
    }

    if (isDevelopment) {
      console.log('BrandingCache: Cleared React Query keys:', clearedQueryKeys);
    }
  } catch (error) {
    console.error('BrandingCache: Failed to clear React Query cache:', error);
  }

  return clearedQueryKeys;
}

/**
 * Orchestrate complete branding cache invalidation
 */
export async function invalidateBrandingCache(
  queryClient: QueryClient,
  hostname?: string,
  reason?: string
): Promise<BrandingInvalidationResult> {
  const startTime = performance.now();
  
  if (isDevelopment) {
    console.log('BrandingCache: Starting invalidation', { hostname, reason });
  }

  const result: BrandingInvalidationResult = {
    success: false,
    keysCleared: [],
    queryKeysCleared: [],
    errors: []
  };

  try {
    // Clear localStorage
    const clearedStorageKeys = hostname 
      ? clearBrandingCacheForHostname(hostname)
      : clearBrandingCache();
    result.keysCleared.push(...clearedStorageKeys);

    // Clear React Query cache
    const clearedQueryKeys = clearBrandingQueries(queryClient, hostname);
    result.queryKeysCleared.push(...clearedQueryKeys);

    result.success = true;
  } catch (error) {
    const errorMsg = `Branding cache invalidation failed: ${error}`;
    result.errors.push(errorMsg);
    console.error('BrandingCache:', errorMsg);
  }

  const duration = performance.now() - startTime;
  
  if (isDevelopment) {
    console.log('BrandingCache: Invalidation completed', {
      ...result,
      durationMs: duration.toFixed(2),
      hostname,
      reason
    });
  }

  return result;
}

/**
 * Check if branding cache needs refresh based on meta-version
 */
export function shouldRefreshBrandingCache(
  hostname: string, 
  currentMetaVersion: string
): boolean {
  try {
    const cached = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (!cached) {
      return true; // No cache, needs refresh
    }

    const parsed: BrandingCacheEntry = JSON.parse(cached);
    
    // Check hostname match
    if (parsed.hostname !== hostname) {
      return true;
    }

    // Check meta-version mismatch
    if (parsed.metaVersion && parsed.metaVersion !== currentMetaVersion) {
      if (isDevelopment) {
        console.log('BrandingCache: Meta-version mismatch', {
          cached: parsed.metaVersion,
          current: currentMetaVersion
        });
      }
      return true;
    }

    // Check TTL expiration
    if (isBrandingCacheExpired(parsed)) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('BrandingCache: Error checking refresh need:', error);
    return true; // On error, force refresh
  }
}

/**
 * Development utility to inspect branding cache state
 */
export function inspectBrandingCache(): void {
  if (!isDevelopment) {
    console.warn('BrandingCache: inspect only available in development');
    return;
  }

  try {
    const cached = localStorage.getItem(BRANDING_STORAGE_KEY);
    const critical = localStorage.getItem(CRITICAL_BRANDING_STORAGE_KEY);

    console.log('BrandingCache: Current state inspection');
    console.log('- Main cache:', cached ? JSON.parse(cached) : 'None');
    console.log('- Critical cache:', critical ? JSON.parse(critical) : 'None');

    if (cached) {
      const parsed: BrandingCacheEntry = JSON.parse(cached);
      const age = Date.now() - parsed.timestamp;
      const expired = isBrandingCacheExpired(parsed);
      console.log(`- Cache age: ${age}ms (expired: ${expired})`);
    }
  } catch (error) {
    console.error('BrandingCache: Failed to inspect cache:', error);
  }
}